import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { StaticContext, ErrorCard, TabletScreen } from '@sm/webassets';
import { defineMessages, T } from '@sm/intl';
import { Card, Typography } from '@sm/wds-react';
import { IconDesktop, IconTrash, IconBadge } from '@sm/wds-icons';
import { getClientEnvironmentDetails } from '@sm/utils';

import NavBar from '../../components/NavBar';
import SearchBox from '../../components/SearchBox';
import RemoveApp from '../../components/RemoveApp';
import AppLink from '../../components/AppLink';
import AppListingSkeleton from '../../components/AppListingList/AppListingSkeleton';
import AppDirectoryBasePage from '../AppDirectoryBasePage';

import installedApplications from './myApps.graphql';
import './my-apps.scss';
import AppPlaceHolderImg from '../../static/images/app-placeholder.png';

const FETCH_LIMIT = 50;

const COPY = defineMessages({
  NO_RESULTS_FOUND: {
    id: 'MyApps.NoResultsFound',
    defaultMessage: 'You currently have no installed apps',
    description:
      '[Type: Paragraph][Vis.: med] - A message informing the current user that there are not installed apps'
  },
  PAGE_TITLE: {
    id: 'MyApps.PageTitle',
    defaultMessage: 'Installed Apps',
    description:
      '[Type: Header][Vis.: high] - The title of the page displaying the installed apps for the current user'
  },
  LAUNCH_APP_LINK: {
    id: 'MyApps.LaunchAppLink',
    defaultMessage: 'Launch',
    description:
      '[Type: Link][Vis.: med] - A link that will direct the user to launch page for a given application'
  },
  APP_DETAILS_LINK: {
    id: 'MyApps.AppDetailsLink',
    defaultMessage: 'Details',
    description:
      '[Type: Link][Vis.: med] - A link that will direct the user to details of a given application'
  },
  REMOVE_APP_LINK: {
    id: 'MyApps.RemoveAppLink',
    defaultMessage: 'Remove',
    description:
      '[Type: Link][Vis.: med] - A link that direct the user to uninstall a given application'
  }
});

function renderCategories(categories) {
  if (!categories || !categories.length) {
    return null;
  }
  return (
    <Typography color="dark-muted">
      {categories.map(c => c.name).join(' ')}
    </Typography>
  );
}

const MyApps = ({ location: { search } }) => {
  const {
    environment: { languageId },
    userAgent
  } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);

  return (
    <AppDirectoryBasePage
      id="App Directory - My Apps"
      navbar={
        <>
          <TabletScreen defaultMatches={isDesktop}>
            <div className="wds-m-b-4">
              <SearchBox />
            </div>
          </TabletScreen>
          <NavBar />
        </>
      }
    >
      <Query
        query={installedApplications}
        variables={{
          language: languageId,
          page: 1,
          pageSize: FETCH_LIMIT
        }}
      >
        {({ loading, error, data, refetch }) => {
          return (
            <>
              <Typography variant="sectionTitle" className="wds-m-b-4">
                <T desc={COPY.PAGE_TITLE} />
              </Typography>
              <Choose>
                <When condition={error}>
                  <ErrorCard data-testid="MyApps__ListingError" />
                </When>
                <When condition={loading}>
                  <AppListingSkeleton data-testid="MyApps__ListingSkeleton" />
                </When>
                <When
                  condition={
                    data && data.installedApplications.items.length === 0
                  }
                >
                  <Card className="sm-my-apps__no-apps-found">
                    <Typography>
                      <T desc={COPY.NO_RESULTS_FOUND} />
                    </Typography>
                  </Card>
                </When>
                <Otherwise>
                  <div
                    className="sm-my-apps__apps-container"
                    data-testid="MyApps__Container"
                  >
                    {data.installedApplications.items.map(app => (
                      <div
                        className="sm-my-apps__app wds-flex wds-flex--between wds-p-5 wds-m-b-3"
                        key={app.id}
                        data-testid="MyApps__AppContainer"
                      >
                        <div className="wds-flex">
                          <div className="wds-m-r-4">
                            <img
                              src={app.logo ? app.logo : AppPlaceHolderImg}
                              alt={app.name}
                              className="sm-my-apps__app-logo"
                            />
                          </div>
                          <div>
                            <Typography component="h3" variant="cardTitle">
                              {app.name}
                            </Typography>
                            {renderCategories(app.categories)}
                          </div>
                        </div>
                        <div className="sm-my-apps__app-links wds-flex">
                          <a
                            href={`${app.links.launch}${search}`}
                            className="sm-my-apps__app-links-link wds-flex wds-flex--y wds-flex--y-center"
                          >
                            <IconDesktop size="lg" />
                            <span className="wds-m-t-2">
                              <T desc={COPY.LAUNCH_APP_LINK} />
                            </span>
                          </a>
                          <AppLink
                            app={app}
                            className="sm-my-apps__app-links-link wds-flex wds-flex--y wds-flex--y-center wds-m-l-7"
                          >
                            <IconBadge size="lg" />
                            <span className="wds-m-t-2">
                              <T desc={COPY.APP_DETAILS_LINK} />
                            </span>
                          </AppLink>
                          <div className="wds-m-l-7 wds-flex wds-flex--y wds-flex--y-center">
                            <If condition={!app.isIntegration}>
                              <RemoveApp
                                appId={app.id}
                                onSuccess={() => refetch()}
                              >
                                <div className="sm-my-apps__app-links-link wds-flex wds-flex--y wds-flex--y-center">
                                  <IconTrash size="lg" />
                                  <span className="wds-m-t-2">
                                    <T desc={COPY.REMOVE_APP_LINK} />
                                  </span>
                                </div>
                              </RemoveApp>
                            </If>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Otherwise>
              </Choose>
            </>
          );
        }}
      </Query>
    </AppDirectoryBasePage>
  );
};

MyApps.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired
};

export default withRouter(MyApps);
